/**
 * One activity, as the preview reads it — plus the two things a kid never sees: its points and its
 * rubric references.
 *
 * Rubrics are REFERENCED, never embedded (B8): `activity.rubrics` is a `string[]` of ids into the
 * catalog at `data.rubrics`, resolved through `store.getRubricCatalog` — a getter that returns `{}`
 * when there is no catalog, so there is no null case. A DANGLING ID IS RENDERED, NEVER SKIPPED:
 * hiding it would make the preview disagree with validation and with the picker.
 *
 * Nothing in this file writes: no action, no id minted, no repair of a dangling reference.
 */
import { computed } from 'vue'

import type { Component, ComputedRef } from 'vue'
import type { Activity } from './interfaces'
import type { ContentBlock, Interaction, InteractionType, ReviewRubric } from './types'

import { withCount } from '@shared/lib/format'

import { useAssessmentEditorStore } from './store'
import {
  INTERACTION_LABELS,
  isTutorObservation,
  TUTOR_OBSERVATION_LABEL,
} from './interaction-labels'
import { INTERACTION_PREVIEWS } from '../../ui/assessments/preview/interactions/registry'

interface PreviewRubricRef {
  key: string
  /** The catalog id — shown raw only when there is no rubric to name. */
  id: string
  name: string
  /** Held by the activity, absent from the catalog (refinement 5). */
  isMissing: boolean
}

/** One standard tagged on the activity. All of them render — showing only the first would be
 *  silent information loss in the surface the author proof-reads. */
interface PreviewStandardRef {
  key: string
  code: string
}

interface UsePreviewActivityReturn {
  refContent: ComputedRef<ContentBlock[]>
  refHasContent: ComputedRef<boolean>
  refInteraction: ComputedRef<Interaction | null>
  refHasInteraction: ComputedRef<boolean>
  /** The registry-resolved component. `null` when there is no interaction, or when the document
   *  carries a type this build has no preview for. */
  refInteractionPreview: ComputedRef<Component | null>
  refInteractionLabel: ComputedRef<string>
  refIsUnknownInteraction: ComputedRef<boolean>
  /** No interaction, but rubrics for the tutor to score while the kid reads aloud. */
  refIsTutorObservation: ComputedRef<boolean>
  /** AUTHOR-ONLY. `null` when the activity is not scored out of anything. */
  refPoints: ComputedRef<number | null>
  /** The interaction's name and the points, as one author-only line; `''` when there is neither.
   *  Composed here rather than in the template, where fragments would fight the whitespace rules. */
  refAuthorNote: ComputedRef<string>
  /** AUTHOR-ONLY. Rubrics are a tutor's review tool — a kid never sees one. */
  refRubrics: ComputedRef<PreviewRubricRef[]>
  refHasRubrics: ComputedRef<boolean>
  /** AUTHOR-ONLY. A curriculum tag is a reporting concern; a kid never sees one. */
  refStandards: ComputedRef<PreviewStandardRef[]>
  refHasStandards: ComputedRef<boolean>
}

const usableArray = <T>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : [])

/** The registry is exhaustive over `InteractionType` BY TYPE, so the compiler cannot see an
 *  `undefined`. At runtime the document can still carry a type from a newer schema, so the lookup
 *  goes through a widened view. */
const previewFor = (type: InteractionType): Component | null =>
  (INTERACTION_PREVIEWS as Partial<Record<InteractionType, Component>>)[type] ?? null

const labelFor = (type: InteractionType): string =>
  (INTERACTION_LABELS as Partial<Record<InteractionType, string>>)[type] ?? type

export const usePreviewActivity = (activity: () => Activity): UsePreviewActivityReturn => {
  const store = useAssessmentEditorStore()

  const refContent = computed<ContentBlock[]>(() =>
    usableArray<ContentBlock>(activity()?.content),
  )

  const refHasContent = computed<boolean>(() => refContent.value.length > 0)

  /** `interaction` is optional in the schema — a prompt-only activity is a legitimate authored
   *  state, not a half-finished one, so this is a `null`, not a warning. */
  const refInteraction = computed<Interaction | null>(() => {
    const interaction = activity()?.interaction
    return interaction && typeof interaction.type === 'string' ? interaction : null
  })

  const refHasInteraction = computed<boolean>(() => refInteraction.value !== null)

  const refInteractionPreview = computed<Component | null>(() => {
    const type = refInteraction.value?.type
    return type ? previewFor(type) : null
  })

  const refIsTutorObservation = computed<boolean>(() =>
    !refHasInteraction.value && isTutorObservation(activity(), store.getRubricCatalog),
  )

  const refInteractionLabel = computed<string>(() => {
    const type = refInteraction.value?.type
    if (type) {
      return labelFor(type)
    }
    return refIsTutorObservation.value ? TUTOR_OBSERVATION_LABEL : ''
  })

  const refIsUnknownInteraction = computed<boolean>(() =>
    refHasInteraction.value && refInteractionPreview.value === null,
  )

  const refPoints = computed<number | null>(() => {
    const points = activity()?.points
    return typeof points === 'number' && Number.isFinite(points) ? points : null
  })

  const refAuthorNote = computed<string>(() => [
    refInteractionLabel.value,
    refPoints.value === null ? '' : withCount(refPoints.value, { one: 'point', other: 'points' }),
  ].filter(Boolean).join(' · '))

  const refRubrics = computed<PreviewRubricRef[]>(() => {
    const catalog = store.getRubricCatalog
    return usableArray<string>(activity()?.rubrics).map((id, index) => {
      const rubric = catalog[id] as ReviewRubric | undefined
      if (!rubric || typeof rubric !== 'object') {
        return {
          key: `rubric-${index}`,
          id,
          // The one place an id is shown: there is no name to show, and the preview must agree
          // with what validation is about to say.
          name: id,
          isMissing: true,
        }
      }
      return {
        key: `rubric-${index}`,
        id,
        name: typeof rubric.name === 'string' && rubric.name.trim() ? rubric.name : 'Untitled rubric',
        isMissing: false,
      }
    })
  })

  const refHasRubrics = computed<boolean>(() => refRubrics.value.length > 0)

  /**
   * Every tagged standard, in document order — including any the picker would no longer offer: the
   * preview shows the document as it IS, so filtering would hide the off-grade tag an author needs
   * to notice. Read defensively; the working document is only validated on demand.
   */
  const refStandards = computed<PreviewStandardRef[]>(() =>
    usableArray<string>(activity()?.standards)
      .filter((code): code is string => typeof code === 'string' && code.trim().length > 0)
      .map((code, index) => ({ key: `standard-${index}`, code })),
  )

  const refHasStandards = computed<boolean>(() => refStandards.value.length > 0)

  return {
    refContent,
    refHasContent,
    refInteraction,
    refHasInteraction,
    refInteractionPreview,
    refInteractionLabel,
    refIsUnknownInteraction,
    refIsTutorObservation,
    refPoints,
    refAuthorNote,
    refRubrics,
    refHasRubrics,
    refStandards,
    refHasStandards,
  }
}
